import React, {useState} from 'react';
import {Box, VStack, useToast} from '@chakra-ui/react';
import {TextG14} from '../../../components/typography';
import Modal from '../../../components/Modals/Modal';
import useAuth from '../../../hooks/useAuth';
import {useUpdateProfile} from '../../../hooks/useUpdateProfile';
import styled from 'styled-components';

const StyledInput = styled.input`
  width: 100%;
  font-family: Gordita;
  font-size: 14px;
  line-height: 20px;
  padding: 14px 16px;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  outline: none;
  &:focus {
    border-color: #1a1a1a;
  }
`;

const SaveButton = styled.button<{$disabled: boolean}>`
  width: 100%;
  padding: 14px;
  border-radius: 28px;
  border: none;
  font-family: Gordita;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: ${(props) => (props.$disabled ? '#E5E5E5' : '#E02F84')};
  box-shadow: ${(props) => (props.$disabled ? 'none' : '0 -4px 0 0 rgba(0, 0, 0, 0.25) inset')};
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.15s ease;
  &:active {
    transform: ${(props) => (props.$disabled ? 'none' : 'scale(0.98)')};
  }
`;

interface EditNicknameModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EditNicknameModal = ({isOpen, onClose}: EditNicknameModalProps) => {
  const {user} = useAuth();
  const updateProfile = useUpdateProfile();
  const [nickname, setNickname] = useState(user?.handle || '');
  const isSaving = updateProfile.isLoading;
  const toast = useToast();

  const handleSave = async () => {
    if (!nickname.trim() || isSaving) return;

    const cleanHandle = nickname.replace(/^@/, '').trim();
    if (cleanHandle === user?.handle) {
      onClose();
      return;
    }

    // Close immediately so the optimistic update drives the UI.
    onClose();
    try {
      await updateProfile.mutateAsync({handle: cleanHandle});
      toast({title: 'Nickname updated!', status: 'success', duration: 2000});
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Could not update nickname';
      toast({title: msg, status: 'error', duration: 3000});
    }
  };

  return (
    <Modal
      show={isOpen}
      parentCallback={() => onClose()}
      closeButton
      closeButtonPosition="right"
      header={
        <TextG14 fontWeight="700" textAlign="center" w="100%" mt="0">
          Nickname
        </TextG14>
      }
      modalPadding="16px 20px 24px">
      <VStack spacing={6} align="stretch" minH="300px" justify="space-between">
        <Box>
          <Box fontSize="11px" fontFamily="Gordita" color="#757D84" mb={1} ml={1}>
            Nickname
          </Box>
          <StyledInput
            value={nickname.startsWith('@') ? nickname : `@${nickname}`}
            onChange={(e) => setNickname(e.target.value.replace(/^@/, ''))}
            placeholder="@username"
            autoFocus
          />
        </Box>
        <SaveButton $disabled={!nickname.trim() || isSaving} onClick={handleSave}>
          {isSaving ? 'Saving...' : 'Save'}
        </SaveButton>
      </VStack>
    </Modal>
  );
};

export default EditNicknameModal;
